import {makeAutoObservable} from "mobx";
import * as SchedulesProvider from '../../services/schedules';
import * as PubSub from '../pubSub';
import {Manager} from "../../core";
import {afterToday, moneyFormat} from "../../utils";

export class SchedulesStoreMobx {
	public list: any[] = [];
	public nextSchedules: any[] = [];
	private title: string = 'Obrigado pela oportunidade';
	private text: string = "Amanhã deixaremos sua casa limpa e cheirosa.";
	constructor() {
		makeAutoObservable(this);
	}

	get = async () => {
		try {
			const {data}: any = await SchedulesProvider.get();
			this.setMessageSchedule(data);
			const mapped = this.setSchedule(data);
			return mapped;
		} catch (err) {
			throw new Manager.ServiceError('error.comboStore.get', err);
		}
	}

	setInterval = (interval?: string): { often: number; count: number } => {
		switch (interval) {
			case 'biweekly':
				return {
					often: 14,
					count: 0,
				};
			case 'weekly':
				return {
					often: 7,
					count: 0,
				};
			default:
				return {
					often: 1,
					count: 1,
				};
		}
	}

	setDate = (date: string, hours?: string, weekday?: string, preferWeekday: boolean = false) => {
		try {
			if (!date && !weekday) return { start: '', end: '' };
			// Support two formats: 'YYYY/MM/DD' (legacy) or ISO strings returned by backend
			let baseDate: Date | null = null;
			if (!preferWeekday && date) {
				if (date.indexOf('/') !== -1) {
					const d = date.split('/');
					const year = Number(d[0]);
					const month = Number(d[1]) - 1;
					const day = Number(d[2]);
					if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
						baseDate = new Date(year, month, day);
					} else {
						baseDate = null;
					}
				} else if (date.includes('-')) {
					const d = date.split('-');
					const year = Number(d[0]);
					const month = Number(d[1]) - 1;
					const day = Number(d[2]);
					if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
						baseDate = new Date(year, month, day);
					}
				} else {
					const parsed = Date.parse(date);
					if (!Number.isNaN(parsed)) baseDate = new Date(parsed);
					else baseDate = null;
				}
			} else if (weekday) {
				// compute next date for given weekday (weekday may be 'Monday'/'Mon' or Portuguese 'segunda')
				const wd = (weekday || '').toString().trim().toLowerCase();
				const names: Record<string, number> = {
					sunday: 0, sun: 0, domingo: 0,
					monday: 1, mon: 1, segunda: 1, 'segunda-feira': 1,
					tuesday: 2, tue: 2, tues: 2, terca: 2, 'terça': 2, 'terça-feira': 2, ter: 2,
					wednesday: 3, wed: 3, quarta: 3, 'quarta-feira': 3, qua: 3,
					thursday: 4, thu: 4, quinta: 4, 'quinta-feira': 4, qui: 4,
					friday: 5, fri: 5, sexta: 5, 'sexta-feira': 5, sex: 5,
					saturday: 6, sat: 6, sabado: 6, sábado: 6, sab: 6
				};
				const target = names[wd];
				if (typeof target === 'number') {
					const today = new Date();
					// zero time part
					const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
					let delta = (target - now.getDay() + 7) % 7;
					// if same day and hours provided, check time to decide if use today or next week
					if (delta === 0 && hours) {
						const m = hours.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
						if (m) {
							let hh = Number(m[1]);
							const mm = Number(m[2]);
							const ampm = (m[3] || '').toLowerCase();
							if (ampm === 'pm' && hh < 12) hh += 12;
							if (ampm === 'am' && hh === 12) hh = 0;
							const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
							if (candidate.getTime() <= Date.now()) delta = 7; // choose next week
						}
						else {
							// no parsable time — default to next week
							delta = 7;
						}
					}
					const targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + delta);
					baseDate = targetDate;
				} else {
					baseDate = null;
				}
			}

			// parse hours like '10:30AM' or '10:30' or '10:30 PM'
			let hour = 0;
			let min = 0;
			if (hours) {
				const m = hours.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
				if (m) {
					hour = Number(m[1]);
					min = Number(m[2]);
					const ampm = (m[3] || '').toLowerCase();
					if (ampm === 'pm' && hour < 12) hour += 12;
					if (ampm === 'am' && hour === 12) hour = 0;
				}
			}

			if (!baseDate) return { start: '', end: '' };

			const startDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hour, min);
			const endDate = new Date(startDate.getTime());
			endDate.setHours(startDate.getHours() + 1);

			// Validate
			if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return { start: '', end: '' };

			return {
				start: startDate.getTime(),
				end: endDate.getTime(),
			};
		} catch (e) {
			return { start: '', end: '' };
		}
	}

	setSchedule = (data: SchedulesProvider.SchedulesItemModel[]) => {
		// First, pre-calculate the absolute first cleaning for each user based on the lowest ID
		const firstCleaningByUser = new Map<string, number>();

		data.forEach((e) => {
			const userId = String(e.userId);
			const currentFirstId = firstCleaningByUser.get(userId);
			if (currentFirstId === undefined || e.id < currentFirstId) {
				firstCleaningByUser.set(userId, e.id);
			}
		});

		const mapped = data.map((e) => {
			// normalize often for case-insensitive comparisons
			const oft = (e?.often || '').toString().toLowerCase();
			const INTERVAL: { often: number; count: number } = this.setInterval(oft);
			const dt = this.setDate(e.date, e.hour, e.weekday, oft !== 'once');
			const START_TIME = dt.start;
			const END_TIME = dt.end;
			// if dates are invalid, skip this item
			if (!START_TIME || !END_TIME) return null as any;

			// Check if this is the absolute first cleaning for this user
			const userFirstId = firstCleaningByUser.get(String(e.userId));
			const isAbsoluteFirst = userFirstId === e.id;

			// build recurrence rule: prefer WEEKLY when often === 'weekly'
			let recurrence = '';
			if (e?.often === 'once' || oft === 'once') {
				// single occurrence — no recurrence rule
				recurrence = '';
			} else if (e?.often === 'weekly' || oft === 'weekly') {
				// try to include BYDAY if weekday is provided
				const wd = (e?.weekday || '').toString().trim().toLowerCase();
				const byMap: Record<string, string> = {
					monday: 'MO', mon: 'MO', 'segunda': 'MO', 'segunda-feira': 'MO',
					tuesday: 'TU', tue: 'TU', terca: 'TU', 'terça': 'TU', 'terça-feira': 'TU',
					wednesday: 'WE', wed: 'WE', quarta: 'WE', 'quarta-feira': 'WE',
					thursday: 'TH', thu: 'TH', quinta: 'TH', 'quinta-feira': 'TH',
					friday: 'FR', fri: 'FR', sexta: 'FR', 'sexta-feira': 'FR',
					saturday: 'SA', sat: 'SA', sabado: 'SA', 'sábado': 'SA',
					sunday: 'SU', sun: 'SU', domingo: 'SU'
				};
				const by = byMap[wd];
				recurrence = `FREQ=WEEKLY;INTERVAL=1;` + (by ? `BYDAY=${by};` : '');
			} else if (e?.often === 'biweekly') {
				const wd = (e?.weekday || '').toString().trim().toLowerCase();
				const byMap: Record<string, string> = {
					monday: 'MO', mon: 'MO', 'segunda': 'MO', 'segunda-feira': 'MO',
					tuesday: 'TU', tue: 'TU', terca: 'TU', 'terça': 'TU', 'terça-feira': 'TU',
					wednesday: 'WE', wed: 'WE', quarta: 'WE', 'quarta-feira': 'WE',
					thursday: 'TH', thu: 'TH', quinta: 'TH', 'quinta-feira': 'TH',
					friday: 'FR', fri: 'FR', sexta: 'FR', 'sexta-feira': 'FR',
					saturday: 'SA', sat: 'SA', sabado: 'SA', 'sábado': 'SA',
					sunday: 'SU', sun: 'SU', domingo: 'SU'
				};
				const by = byMap[wd];
				recurrence = `FREQ=WEEKLY;INTERVAL=2;` + (by ? `BYDAY=${by};` : '');
			} else {
				// fallback: daily with provided numeric interval (no COUNT to allow continuous recurrence)
				recurrence = `FREQ=DAILY;INTERVAL=${INTERVAL.often};`;
			}

			const statusNorm = (e?.status || '').toString().toUpperCase();
			const categoryColor = statusNorm === 'CANCELED' ? '#bdbdbd' : '#1aaa55';
			const oftenTranslated = (() => {
				const o = (e?.often || '').toString().toLowerCase();
				if (o === 'biweekly') return 'Quinzenal';
				if (o === 'weekly') return 'Semanal';
				if (o === 'once') return 'Uma limpeza';
				return o;
			})();

			return {
				Id: e.id,
				Subject: `${statusNorm === 'DELIVERED' ? '[ENTREGUE] ' : ''}Plano contratado ${e.plan} - ${oftenTranslated}\n${moneyFormat(e.value)}`,
				Location: `${e.street}, ${e.number} - ${e.district} - ${e.uf} - ${e.complement || ''} `,
				StartTime: START_TIME,
				EndTime: END_TIME,
				Description: `${e.bedrooms} quartos | ${e.bathrooms} banheiros | ${e.middle} lavabos\nObs.: ${e.ps}\nContato: ${e.name}\n${e.cellphone}`,
				CategoryColor: categoryColor,
				Status: statusNorm,
				RecurrenceRule: recurrence,
				isAbsoluteFirst,
				// include original fields so modal can display structured data
				name: (e as any).name,
				surname: (e as any).surname,
				cellphone: (e as any).cellphone,
				fixphone: (e as any).fixphone,
				city: (e as any).city,
				street: (e as any).street,
				number: (e as any).number,
				complement: (e as any).complement,
				apartment: (e as any).apartment,
				block: (e as any).block,
				neighborhood: (e as any).neighborhood || (e as any).district,
				uf: (e as any).uf,
				zipcode: (e as any).zipcode,
				plan: (e as any).plan,
				often: (() => {
					const o = (e as any).often?.toString().toLowerCase();
					if (o === 'biweekly') return 'Quinzenal';
					if (o === 'weekly') return 'Semanal';
					if (o === 'once') return 'Uma limpeza';
					return o;
				})(),
				value: moneyFormat((e as any).value),
				payment: (e as any).payment,
				ps: (e as any).ps,
				email: (e as any).email,
				bedrooms: (e as any).bedrooms,
				bathrooms: (e as any).bathrooms,
				middle: (e as any).middle,
				hour: (e as any).hour,
				userPresent: (e as any).userPresent,
				leaveKey: (e as any).leaveKey,
				electronicLock: (e as any).electronicLock,
				code: (e as any).code,
				noPets: (e as any).noPets || (e as any).noPats,
				freePets: (e as any).freePets,
				trappedPets: (e as any).trappedPets,
				allergic: (e as any).allergic,
				worksOfArt: (e as any).worksOfArt,
				plants: (e as any).plants,
				internalCleaning: (e as any).internalCleaning,
				userId: e.userId,
				fcmId: (e as any).fcmId || (e as any).fmcId || (e as any).key 
			}
		});
		return mapped.filter(Boolean);
	}

	setMessageSchedule = (list: any) => {
		const tomorrow = afterToday();
		const tomorrowDate = new Date();
		tomorrowDate.setDate(tomorrowDate.getDate() + 1);
		const tomorrowWeekday = tomorrowDate.getDay(); // 0 is Sunday, 1 is Monday...

		const dayNamesMap: Record<number, string[]> = {
			0: ['sunday', 'sun', 'domingo'],
			1: ['monday', 'mon', 'segunda', 'segunda-feira'],
			2: ['tuesday', 'tue', 'terça', 'terça-feira', 'terca'],
			3: ['wednesday', 'wed', 'quarta', 'quarta-feira'],
			4: ['thursday', 'thu', 'quinta', 'quinta-feira'],
			5: ['friday', 'fri', 'sexta', 'sexta-feira'],
			6: ['saturday', 'sat', 'sábado', 'sabado']
		};

		const allowedNames = dayNamesMap[tomorrowWeekday];

		let schedule: any[] = [];
		if (!list) return [];
		
		schedule = list.filter((e: any) => {
			if (e.status === 'CANCELED') return false;
			
			const matchesDirectDate = e.date === tomorrow;
			
			const oft = String(e.often || '').toLowerCase();
			const isWeekly = oft === 'weekly';
			const isBiWeekly = oft === 'biweekly';
			
			const wd = String(e.weekday || '').toLowerCase().trim();
			const matchesWeekday = allowedNames.includes(wd);

			// For the simple alert, we include all weekly matches for tomorrow
			// and all direct date matches (which covers 'once' cleanings)
			return matchesDirectDate || (isWeekly && matchesWeekday) || (isBiWeekly && matchesWeekday);
		});

		this.nextSchedules = schedule.length > 0 ? schedule.map((e: any) => {
			return {
					userId: e.userId,
					date: e.date,
					plan: e.plan,
					title: this.title,
					text: this.text,
					key: e.fcmId || e.fmcId || e.key,
				};
		}): [];
	}
}

let singleton: SchedulesStoreMobx | null;

export const SchedulesStore = () => {
	if (!singleton) {
		singleton = new SchedulesStoreMobx();

		PubSub.subscribe(PubSub.LOGOUT, () => {
			singleton = null;
		});
	}
	return singleton;
};
